import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

const getSocketUrl = () => {
  const envUrl = (window as any)?.ENV?.REACT_APP_SOCKET_URL;
  if (envUrl && envUrl !== '/') {
    return envUrl;
  }
  return window.location.origin;
};

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onlineUsers: OnlineUser[];
  joinChat: (contactId: string) => void;
  leaveChat: (contactId: string) => void;
  sendTypingStart: (contactId: string, whatsAppNumberId: string) => void;
  sendTypingStop: (contactId: string, whatsAppNumberId: string) => void;
  markMessagesRead: (contactId: string) => void;
}

interface OnlineUser {
  userId: string;
  name: string;
  email: string;
  role: string;
  connectedAt: string;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const { state: authState } = useAuth();

  useEffect(() => {
    if (authState.isAuthenticated && authState.token) {
      const socketUrl = getSocketUrl();
      
      const newSocket = io(socketUrl, {
        auth: {
          token: authState.token,
        },
        transports: ['websocket', 'polling'],
        withCredentials: true,
      });

      newSocket.on('connect', () => {
        console.log('🔌 Connected to server');
        setIsConnected(true);
        newSocket.emit('get_online_users');
      });

      newSocket.on('disconnect', () => {
        console.log('🔌 Disconnected from server');
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('🔌 Connection error:', error.message || error);
        console.log('🔎 Debug:', { url: socketUrl });
        setIsConnected(false);
      });

      // Handle online/offline users
      newSocket.on('online_users', (users: OnlineUser[]) => {
        setOnlineUsers(users);
      });

      newSocket.on('user_online', (user: OnlineUser) => {
        setOnlineUsers(prev => {
          const exists = prev.find(u => u.userId === user.userId);
          return exists ? prev : [...prev, user];
        });
      });

      newSocket.on('user_offline', (user: { userId: string }) => {
        setOnlineUsers(prev => prev.filter(u => u.userId !== user.userId));
      });

      // Handle chat events
      newSocket.on('chat_joined', (data) => {
        console.log('💬 Joined chat:', data.contactId, 'with WhatsApp:', data.whatsAppNumberId, 'room:', data.chatRoom);
      });

      newSocket.on('chat_left', (data) => {
        console.log('💬 Left chat:', data.contactId);
      });

      // Handle conversation status updates
      newSocket.on('conversation_status_updated', (data) => {
        console.log('📡 WebSocket received conversation_status_updated:', data);
        // Emit a custom event that components can listen to
        window.dispatchEvent(new CustomEvent('conversationStatusUpdated', { 
          detail: data 
        }));
        console.log('📡 Dispatched conversationStatusUpdated custom event');
      });

      // Handle bot hold status changes
      newSocket.on('bot_hold_status_changed', (data) => {
        console.log('🤖 Bot hold status changed:', data);
        // Dispatch custom event for components to handle
        window.dispatchEvent(new CustomEvent('botHoldStatusChanged', { 
          detail: data 
        }));
      });

      // Handle notifications
      newSocket.on('new_notification', (notification) => {
        console.log('📢 New notification received:', notification);
        // Dispatch custom event for notification context to handle
        window.dispatchEvent(new CustomEvent('newNotification', { 
          detail: notification 
        }));
      });

      newSocket.on('notification_count', (data) => {
        console.log('📊 Notification count updated:', data.unreadCount);
        // Dispatch custom event for notification context to handle
        window.dispatchEvent(new CustomEvent('notificationCountUpdate', { 
          detail: data 
        }));
      });

      // Handle errors
      newSocket.on('error', (error) => {
        console.error('🔌 Socket error:', error);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
        setSocket(null);
        setIsConnected(false);
        setOnlineUsers([]);
      };
    }
  }, [authState.isAuthenticated, authState.token]);

  const joinChat = (contactId: string) => {
    if (socket) {
      socket.emit('join_chat', { contactId });
    }
  };

  const leaveChat = (contactId: string) => {
    if (socket) {
      socket.emit('leave_chat', { contactId });
    }
  };

  const sendTypingStart = (contactId: string, whatsAppNumberId: string) => {
    if (socket) {
      socket.emit('typing_start', { contactId, whatsAppNumberId });
    }
  };

  const sendTypingStop = (contactId: string, whatsAppNumberId: string) => {
    if (socket) {
      socket.emit('typing_stop', { contactId, whatsAppNumberId });
    }
  };

  const markMessagesRead = (contactId: string) => {
    if (socket) {
      socket.emit('mark_messages_read', { contactId });
    }
  };

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        onlineUsers,
        joinChat,
        leaveChat,
        sendTypingStart,
        sendTypingStop,
        markMessagesRead,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
